// ─── Slate / timecode helpers (pure, unit-tested) ───────────────────────────

/** 0-based index → "SCENE 01", "SCENE 02", … (zero-padded to 2). */
export function formatSceneNumber(index) {
  return `SCENE ${String(index + 1).padStart(2, '0')}`;
}

/**
 * Category name → short uppercase slate title.
 * "Scene 02 · Production" → "PRODUCTION". No separator → whole string uppercased.
 * Empty/null/undefined → "FEATURE".
 */
export function sceneTitleFromCategory(category) {
  if (!category || typeof category !== 'string') return 'FEATURE';
  const parts = category.split('·');
  const tail = parts.length > 1 ? parts[parts.length - 1] : parts[0];
  const trimmed = tail.trim();
  return trimmed ? trimmed.toUpperCase() : 'FEATURE';
}

/**
 * Deterministic faux film timecode HH:MM:SS:FF from a card index.
 * Base offset + index * fixed step at 24fps. Same index → same output.
 */
export function formatTimecode(index) {
  const FPS = 24;
  const baseFrames = 24 * FPS;            // start at 00:00:24:00
  const stepFrames = 137;                 // arbitrary per-scene advance
  let totalFrames = baseFrames + index * stepFrames;

  const ff = totalFrames % FPS;
  let totalSeconds = Math.floor(totalFrames / FPS);
  const ss = totalSeconds % 60;
  let totalMinutes = Math.floor(totalSeconds / 60);
  const mm = totalMinutes % 60;
  const hh = Math.floor(totalMinutes / 60) % 24;

  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}:${pad(ff)}`;
}

const pageSelector = '[data-printale-faq-page]';
const sourceSelector = '[data-printale-faq-source]';
const stageSelector = '[data-printale-faq-stage]';
const categoriesSelector = '[data-printale-faq-categories]';
const listSelector = '[data-printale-faq-list]';
const threeSelector = '[data-printale-faq-three]';
const threeCanvasSelector = '[data-printale-faq-three-canvas]';
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const categoryRules = [
  {
    name: 'Scene 03 · Delivery',
    keywords: ['physical', 'digital', 'ship', 'australia', 'receive', 'download', 'prints'],
  },
  {
    name: 'Scene 04 · Cast & Details',
    keywords: ['pets', 'multiple', 'people', 'couples', 'friends', 'families', 'solo'],
  },
  {
    name: 'Scene 02 · Production',
    keywords: ['long', 'take', 'revision', 'exactly', 'look', 'designed', 'stylised', 'adjusting'],
  },
  {
    name: 'Scene 01 · Getting Started',
    keywords: ['work', 'poster', 'create', 'theme', 'idea', 'photo', 'questionnaire'],
  },
];

const categoryDisplayOrder = [
  'Scene 01 · Getting Started',
  'Scene 02 · Production',
  'Scene 03 · Delivery',
  'Scene 04 · Cast & Details',
  'Scene 05 · Fine Print',
];

function normalise(text) {
  return text.replace(/\s+/g, ' ').trim();
}

function categoryFor(question, answer) {
  const haystack = `${question} ${answer}`.toLowerCase();
  return categoryRules.find((category) => category.keywords.some((keyword) => {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escapedKeyword}([^a-z0-9]|$)`, 'i').test(haystack);
  }))?.name || 'Scene 05 · Fine Print';
}

function createAnswerHtml(nodes, fallbackAnswer) {
  if (nodes.length) {
    return nodes.map((node) => node.outerHTML || `<p>${normalise(node.textContent || '')}</p>`).join('');
  }

  return `<p>${fallbackAnswer}</p>`;
}

function extractFaqItems(source) {
  const children = [...source.children].filter((child) => normalise(child.textContent || ''));
  const headingItems = [];

  children.forEach((child) => {
    const childText = normalise(child.textContent || '');
    const strongText = normalise(child.querySelector('strong')?.textContent || '');
    const isQuestionParagraph = child.tagName === 'P' && strongText && strongText === childText && childText.endsWith('?');

    if (/^H[2-6]$/.test(child.tagName) || isQuestionParagraph) {
      headingItems.push({ question: normalise(child.textContent || ''), nodes: [] });
      return;
    }

    if (headingItems.length) {
      headingItems[headingItems.length - 1].nodes.push(child.cloneNode(true));
    }
  });

  if (headingItems.length) {
    return headingItems
      .map((item) => ({
        question: item.question,
        answerHtml: createAnswerHtml(item.nodes, ''),
        answerText: normalise(item.nodes.map((node) => node.textContent || '').join(' ')),
      }))
      .filter((item) => item.question && item.answerText);
  }

  const lines = normalise(source.innerText || '')
    .split(/(?<=\?)\s+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const items = [];
  lines.forEach((line) => {
    const questionMatch = line.match(/^(.+?\?)(.*)$/);
    if (!questionMatch) return;
    const question = normalise(questionMatch[1]);
    const answer = normalise(questionMatch[2]);
    if (question && answer) {
      items.push({ question, answerHtml: `<p>${answer}</p>`, answerText: answer });
    }
  });

  return items;
}

function renderFaq(page, stage, categoriesContainer, list, items) {
  const presentCategories = new Set(items.map((item) => item.category));
  const categories = ['All scenes', ...categoryDisplayOrder.filter((category) => presentCategories.has(category))];

  categoriesContainer.innerHTML = categories.map((category, index) => `
    <button
      class="printale-faq-category${index === 0 ? ' is-active' : ''}"
      type="button"
      data-printale-faq-filter="${category}"
    >
      ${category}
    </button>
  `).join('');

  list.innerHTML = items.map((item, index) => `
    <details class="printale-faq-card" data-printale-faq-category="${item.category}" ${index === 0 ? 'open' : ''}>
      <summary>
        <span class="printale-faq-slate" aria-hidden="true">
          <span class="printale-faq-slate__clap">
            <span class="printale-faq-slate__stripes"></span>
          </span>
          <span class="printale-faq-slate__body">
            <span class="printale-faq-slate__scene">${formatSceneNumber(index)}</span>
            <span class="printale-faq-slate__title">${sceneTitleFromCategory(item.category)}</span>
            <span class="printale-faq-slate__meta">TAKE 1 · ${formatTimecode(index)}</span>
          </span>
        </span>
        <span class="printale-faq-question">${item.question}</span>
        <span class="printale-faq-toggle" aria-hidden="true"></span>
      </summary>
      <div class="printale-faq-answer">${item.answerHtml}</div>
    </details>
  `).join('');

  categoriesContainer.addEventListener('click', (event) => {
    const button = event.target.closest('[data-printale-faq-filter]');
    if (!button) return;

    const selected = button.getAttribute('data-printale-faq-filter');
    categoriesContainer.querySelectorAll('.printale-faq-category').forEach((categoryButton) => {
      categoryButton.classList.toggle('is-active', categoryButton === button);
    });

    list.querySelectorAll('.printale-faq-card').forEach((card) => {
      const show = selected === 'All scenes' || card.getAttribute('data-printale-faq-category') === selected;
      card.hidden = !show;
      if (!show) card.removeAttribute('open');
    });
  });

  // Beam connector element (lights the open card from the booth side).
  const beam = document.createElement('div');
  beam.className = 'printale-faq-beam';
  beam.setAttribute('aria-hidden', 'true');
  list.prepend(beam);

  function positionBeamTo(card) {
    const listRect = list.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const y = cardRect.top - listRect.top + cardRect.height / 2;
    list.style.setProperty('--faq-beam-y', `${y}px`);
    beam.classList.add('is-lit');
  }

  function playReel(card) {
    // A small clapper movement keeps the cinema cue without flashing the answer.
    card.classList.remove('is-projecting');
    card.classList.add('is-clapping');
    positionBeamTo(card);
    card.addEventListener('animationend', () => {
      card.classList.remove('is-clapping');
    }, { once: true });
    window.setTimeout(() => {
      card.classList.remove('is-clapping');
    }, 320);
  }

  const cards = [...list.querySelectorAll('.printale-faq-card')];

  function resetAnswerStyles(answer) {
    answer.style.height = '';
    answer.style.overflow = '';
    answer.style.opacity = '';
    answer.style.transform = '';
    answer.style.transformOrigin = '';
  }

  function moveBeamToNearestOpenCard() {
    const openCard = cards.find((card) => card.open && !card.hidden);
    if (openCard) {
      positionBeamTo(openCard);
    } else {
      beam.classList.remove('is-lit');
    }
  }

  function animateCard(card, shouldOpen) {
    const answer = card.querySelector('.printale-faq-answer');
    // Skip JS height animation on mobile — height keyframes trigger layout reflow per frame
    const canAnimate = answer && typeof answer.animate === 'function'
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && window.innerWidth >= 768;

    if (!canAnimate) {
      card.open = shouldOpen;
      if (shouldOpen) playReel(card);
      if (!shouldOpen) moveBeamToNearestOpenCard();
      return;
    }

    if (card.dataset.faqAnimating === 'true') return;
    card.dataset.faqAnimating = 'true';
    card.classList.add('is-faq-animating');

    if (shouldOpen) {
      card.open = true;
      playReel(card);
    }

    const fullHeight = answer.scrollHeight;
    answer.style.overflow = 'hidden';
    answer.style.transformOrigin = 'top center';
    answer.style.height = shouldOpen ? '0px' : `${fullHeight}px`;
    answer.style.opacity = shouldOpen ? '0' : '1';

    const keyframes = shouldOpen
      ? [
        { height: '0px', opacity: 0, transform: 'translateY(-10px) scaleY(0.82)' },
        { height: `${Math.round(fullHeight * 1.04)}px`, opacity: 1, transform: 'translateY(4px) scaleY(1.05)', offset: 0.58 },
        { height: `${Math.round(fullHeight * 0.98)}px`, opacity: 1, transform: 'translateY(-2px) scaleY(0.98)', offset: 0.78 },
        { height: `${fullHeight}px`, opacity: 1, transform: 'translateY(0) scaleY(1)' },
      ]
      : [
        { height: `${fullHeight}px`, opacity: 1, transform: 'translateY(0) scaleY(1)' },
        { height: `${Math.round(fullHeight * 0.62)}px`, opacity: 0.92, transform: 'translateY(-2px) scaleY(0.97)', offset: 0.42 },
        { height: '0px', opacity: 0, transform: 'translateY(-6px) scaleY(0.9)' },
      ];

    const animation = answer.animate(keyframes, {
      duration: shouldOpen ? 560 : 360,
      easing: shouldOpen ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)',
    });

    animation.onfinish = () => {
      if (!shouldOpen) card.open = false;
      resetAnswerStyles(answer);
      delete card.dataset.faqAnimating;
      card.classList.remove('is-faq-animating');
      if (!shouldOpen) moveBeamToNearestOpenCard();
    };

    animation.oncancel = () => {
      resetAnswerStyles(answer);
      delete card.dataset.faqAnimating;
      card.classList.remove('is-faq-animating');
      if (!shouldOpen) moveBeamToNearestOpenCard();
    };
  }

  // Independent reels: cards stay open until the user toggles them closed.
  cards.forEach((card) => {
    const summary = card.querySelector('summary');
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      animateCard(card, !card.open);
    });
  });

  // Light the initially-open card on load.
  const openCard = cards.find((card) => card.open);
  if (openCard) positionBeamTo(openCard);

  stage.hidden = false;
  page.classList.add('is-enhanced');
}

function createProjectorScene(THREE, container, canvas, buildPostFX = null) {
  const scene = new THREE.Scene();
  const isMobile = window.innerWidth < 768;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: !isMobile,
    powerPreference: 'high-performance',
  });

  renderer.setPixelRatio(isMobile ? 1.0 : Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = !isMobile;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.setClearColor(0x000000, 0);


  const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
  camera.position.set(0.5, 1.0, 7.6);
  camera.lookAt(0.15, 0.1, 0);

  // Build postfx composer if provided by caller
  let composer = buildPostFX ? buildPostFX(renderer, scene, camera) : null;

  scene.add(new THREE.AmbientLight(0xffd4b0, 1.3));

  const keyLight = new THREE.DirectionalLight(0xfff0c8, 3.6);
  keyLight.position.set(-3.6, 4.8, 5.2);
  keyLight.castShadow = true;
  keyLight.shadow.mapSize.set(1024, 1024);
  scene.add(keyLight);

  const rimLight = new THREE.PointLight(0xff8f83, 8.4, 10);
  rimLight.position.set(3.1, 1.7, 2.6);
  scene.add(rimLight);

  const lensLight = new THREE.PointLight(0xffdc91, 3.6, 7);
  lensLight.position.set(1.85, -0.12, 1.1);
  scene.add(lensLight);

  const root = new THREE.Group();
  root.rotation.set(-0.1, -0.34, 0.035);
  scene.add(root);

  const materials = {
    body: new THREE.MeshStandardMaterial({
      color: 0x7c2731,
      roughness: 0.48,
      metalness: 0.18,
    }),
    bodyDark: new THREE.MeshStandardMaterial({
      color: 0x2a0c11,
      roughness: 0.58,
      metalness: 0.2,
    }),
    trim: new THREE.MeshStandardMaterial({
      color: 0xffb66d,
      roughness: 0.28,
      metalness: 0.35,
      emissive: 0x5f260b,
      emissiveIntensity: 0.24,
    }),
    glass: new THREE.MeshPhysicalMaterial({
      color: 0xffcf86,
      roughness: 0.16,
      metalness: 0.05,
      transmission: 0.12,
      thickness: 0.35,
      transparent: true,
      opacity: 0.96,
      emissive: 0xffb056,
      emissiveIntensity: 0.55,
    }),
    reel: new THREE.MeshStandardMaterial({
      color: 0x521920,
      roughness: 0.42,
      metalness: 0.32,
    }),
    reelFace: new THREE.MeshStandardMaterial({
      color: 0xad4350,
      roughness: 0.38,
      metalness: 0.24,
    }),
    beam: new THREE.MeshBasicMaterial({
      color: 0xffd992,
      transparent: true,
      opacity: 0.11,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    }),
  };

  function createRoundedBox(width, height, depth, radius, material) {
    const x = -width / 2;
    const y = -height / 2;
    const shape = new THREE.Shape();

    shape.moveTo(x + radius, y);
    shape.lineTo(x + width - radius, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + radius);
    shape.lineTo(x + width, y + height - radius);
    shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    shape.lineTo(x + radius, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - radius);
    shape.lineTo(x, y + radius);
    shape.quadraticCurveTo(x, y, x + radius, y);

    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth,
      bevelEnabled: true,
      bevelSegments: 8,
      bevelSize: 0.045,
      bevelThickness: 0.045,
      curveSegments: 18,
    });

    geometry.center();
    return new THREE.Mesh(geometry, material);
  }

  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(2.8, 48),
    new THREE.MeshBasicMaterial({ color: 0x120507, transparent: true, opacity: 0.32 }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.set(-0.35, -1.38, 0.05);
  shadow.scale.set(1.45, 0.36, 1);
  root.add(shadow);

  const body = createRoundedBox(2.52, 1.12, 1.18, 0.22, materials.body);
  body.position.set(-0.34, -0.36, 0);
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  const facePlate = createRoundedBox(2.22, 0.86, 0.08, 0.16, materials.bodyDark);
  facePlate.position.set(-0.36, -0.34, 0.63);
  facePlate.castShadow = true;
  root.add(facePlate);

  const bodyTop = createRoundedBox(2.0, 0.24, 1.02, 0.12, materials.bodyDark);
  bodyTop.position.set(-0.48, 0.34, 0.01);
  bodyTop.castShadow = true;
  root.add(bodyTop);

  const highlight = createRoundedBox(1.62, 0.08, 0.04, 0.035, materials.trim);
  highlight.position.set(-0.55, 0.03, 0.68);
  root.add(highlight);

  const footLeft = createRoundedBox(0.34, 0.34, 0.9, 0.08, materials.bodyDark);
  footLeft.position.set(-1.25, -1.05, 0);
  footLeft.castShadow = true;
  root.add(footLeft);

  const footRight = footLeft.clone();
  footRight.position.x = 0.52;
  root.add(footRight);

  const lensOuter = new THREE.Mesh(new THREE.CylinderGeometry(0.47, 0.6, 0.56, 64), materials.trim);
  lensOuter.rotation.z = Math.PI / 2;
  lensOuter.position.set(1.2, -0.3, 0.08);
  lensOuter.castShadow = true;
  root.add(lensOuter);

  const lensInner = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.42, 0.2, 64), materials.bodyDark);
  lensInner.rotation.z = Math.PI / 2;
  lensInner.position.set(1.5, -0.3, 0.08);
  root.add(lensInner);

  const lensGlass = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.31, 0.11, 64), materials.glass);
  lensGlass.rotation.z = Math.PI / 2;
  lensGlass.position.set(1.67, -0.3, 0.08);
  root.add(lensGlass);

  const beam = new THREE.Mesh(new THREE.ConeGeometry(1.1, 4.3, 64, 1, true), materials.beam);
  beam.rotation.z = -Math.PI / 2;
  beam.position.set(3.08, -0.28, 0.08);
  beam.scale.set(1, 0.66, 0.74);
  root.add(beam);

  function createReel(x, y, scale = 1) {
    const reel = new THREE.Group();
    reel.position.set(x, y, 0.02);
    reel.scale.setScalar(scale);

    const backDisk = new THREE.Mesh(new THREE.CylinderGeometry(0.56, 0.56, 0.1, 64), materials.bodyDark);
    backDisk.rotation.x = Math.PI / 2;
    backDisk.position.z = -0.03;
    reel.add(backDisk);

    const disk = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.12, 64), materials.reel);
    disk.rotation.x = Math.PI / 2;
    disk.castShadow = true;
    reel.add(disk);

    const face = new THREE.Mesh(new THREE.TorusGeometry(0.37, 0.035, 16, 64), materials.reelFace);
    face.position.z = 0.09;
    reel.add(face);

    const outerLip = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.026, 16, 64), materials.trim);
    outerLip.position.z = 0.1;
    reel.add(outerLip);

    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.16, 32), materials.trim);
    hub.rotation.x = Math.PI / 2;
    hub.position.z = 0.13;
    reel.add(hub);

    for (let index = 0; index < 6; index += 1) {
      const angle = (index / 6) * Math.PI * 2;
      const spoke = createRoundedBox(0.12, 0.48, 0.035, 0.035, materials.bodyDark);
      spoke.position.set(Math.cos(angle) * 0.17, Math.sin(angle) * 0.17, 0.14);
      spoke.rotation.z = angle;
      reel.add(spoke);
    }

    root.add(reel);
    return reel;
  }

  const reelOne = createReel(-1.05, 0.85, 1.04);
  const reelTwo = createReel(0.08, 0.92, 0.92);

  const filmBridge = createRoundedBox(1.14, 0.12, 0.14, 0.04, materials.bodyDark);
  filmBridge.position.set(-0.48, 0.88, -0.02);
  filmBridge.rotation.z = 0.06;
  root.add(filmBridge);

  const rearArm = createRoundedBox(0.2, 1.1, 0.18, 0.05, materials.bodyDark);
  rearArm.position.set(-1.03, 0.25, -0.1);
  rearArm.rotation.z = -0.08;
  root.add(rearArm);

  const frontArm = createRoundedBox(0.2, 1.06, 0.18, 0.05, materials.bodyDark);
  frontArm.position.set(0.08, 0.25, -0.1);
  frontArm.rotation.z = 0.08;
  root.add(frontArm);

  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(8, 4),
    new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.22 }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -1.4;
  floor.receiveShadow = true;
  scene.add(floor);

  let pointerX = 0;
  let pointerY = 0;
  let width = 1;
  let height = 1;
  let animationFrame = 0;
  let isVisible = true;
  let cachedRect = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function updateCachedRect() {
    cachedRect = container.getBoundingClientRect();
  }

  function resize() {
    updateCachedRect();
    width = Math.max(1, cachedRect.width);
    height = Math.max(1, cachedRect.height);
    renderer.setSize(width, height, false);
    if (composer) composer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  function onPointerMove(event) {
    if (!cachedRect) updateCachedRect();
    pointerX = ((event.clientX - cachedRect.left) / cachedRect.width - 0.5) * 2;
    pointerY = ((event.clientY - cachedRect.top) / cachedRect.height - 0.5) * 2;
  }

  function animate(time = 0) {
    if (!isVisible) {
      animationFrame = 0;
      return;
    }
    const elapsed = time * 0.001;
    const motion = reducedMotion ? 0 : 1;

    reelOne.rotation.z = elapsed * 0.55 * motion;
    reelTwo.rotation.z = -elapsed * 0.42 * motion;
    beam.material.opacity = 0.095 + Math.sin(elapsed * 1.4) * 0.018 * motion;
    lensLight.intensity = 3.2 + Math.sin(elapsed * 1.8) * 0.38 * motion;
    root.rotation.y += ((-0.33 + pointerX * 0.075) - root.rotation.y) * 0.04;
    root.rotation.x += ((-0.1 - pointerY * 0.04) - root.rotation.x) * 0.04;

    if (composer) {
      composer.render();
    } else {
      renderer.render(scene, camera);
    }
    animationFrame = window.requestAnimationFrame(animate);
  }

  resize();
  container.classList.add('is-three-ready');
  window.addEventListener('resize', resize, { passive: true });
  container.addEventListener('pointermove', onPointerMove, { passive: true });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      isVisible = entry.isIntersecting;
      if (isVisible) {
        if (!animationFrame) {
          animationFrame = window.requestAnimationFrame(animate);
        }
      } else {
        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
          animationFrame = 0;
        }
      }
    });
  }, { threshold: 0.05 });
  observer.observe(container);

  return () => {
    observer.disconnect();
    window.cancelAnimationFrame(animationFrame);
    window.removeEventListener('resize', resize);
    container.removeEventListener('pointermove', onPointerMove);
    if (composer) composer.dispose();
    renderer.dispose();
  };
}

async function initThreeBooth(page) {
  const container = page.querySelector(threeSelector);
  const canvas = page.querySelector(threeCanvasSelector);

  if (!container || !canvas) return;

  // Skip Three.js initialization altogether on mobile screens (<768px) to avoid loading overhead
  if (window.innerWidth < 768) {
    container.style.display = 'none';
    return;
  }

  try {
    const threeSpecifier = 'three';
    const postfxSpecifier = 'printale-postfx';
    const [THREE, postfxMod, engineMod] = await Promise.allSettled([
      import(threeSpecifier),
      import(postfxSpecifier),
      import('./printale-engine.js'),
    ]);

    const three = THREE.status === 'fulfilled' ? THREE.value : null;
    if (!three) throw new Error('Three.js failed to load');

    let buildPostFX = null;
    if (postfxMod.status === 'fulfilled' && engineMod.status === 'fulfilled') {
      const { buildCinematicComposer } = postfxMod.value;
      const { quality } = engineMod.value;
      if (quality.tier !== 'none') {
        buildPostFX = (renderer, scene, camera) =>
          buildCinematicComposer(renderer, scene, camera, quality);
      }
    }

    createProjectorScene(three, container, canvas, buildPostFX);
  } catch (error) {
    container.setAttribute('data-three-error', 'true');
  }
}

function initFaqPage(page) {
  const source = page.querySelector(sourceSelector);
  const stage = page.querySelector(stageSelector);
  const categoriesContainer = page.querySelector(categoriesSelector);
  const list = page.querySelector(listSelector);

  if (!source || !stage || !categoriesContainer || !list) return;

  const items = extractFaqItems(source).map((item) => ({
    ...item,
    category: categoryFor(item.question, item.answerText),
  }));

  if (!items.length) return;

  renderFaq(page, stage, categoriesContainer, list, items);
  initHeroParticles(page);
  initThreeBooth(page);
  initCredits(page);
  initCreditsSmoke(page);
}

function initCredits(page) {
  const credits = page.querySelector('[data-printale-faq-credits]');
  if (!credits) return;

  if (prefersReducedMotion()) {
    credits.classList.add('is-rolling');
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        credits.classList.add('is-rolling');
        observer.disconnect();
      }
    });
  }, { threshold: 0.25 });

  observer.observe(credits);
}

function setupCanvas(canvas, rect) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  const pixelWidth = Math.round(width * dpr);
  const pixelHeight = Math.round(height * dpr);

  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }

  const context = canvas.getContext('2d');
  if (context) context.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { context, width, height };
}

function initHeroParticles(page) {
  if (prefersReducedMotion() || page.querySelector('.printale-faq-particles')) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'printale-faq-particles';
  canvas.setAttribute('aria-hidden', 'true');
  page.prepend(canvas);

  const particleCount = window.innerWidth < 768
    ? Math.min(28, Math.round(window.innerWidth / 14))
    : Math.min(120, Math.max(72, Math.round(window.innerWidth / 14)));
  const particles = Array.from({ length: particleCount }, () => ({
    x: Math.random(),
    y: Math.random(),
    radius: 0.9 + Math.random() * 2.8,
    driftX: -0.36 + Math.random() * 0.72,
    driftY: -0.22 + Math.random() * 0.44,
    phase: Math.random() * Math.PI * 2,
    warmth: Math.random(),
  }));

  const pointer = { x: 0.5, y: 0.34, targetX: 0.5, targetY: 0.34 };
  let canvasRect = canvas.getBoundingClientRect();
  let pageRect = page.getBoundingClientRect();
  let isVisible = true;
  let animationFrame = 0;

  const onResize = () => {
    canvasRect = canvas.getBoundingClientRect();
    pageRect = page.getBoundingClientRect();
  };

  const onPointerMove = (event) => {
    if (!pageRect) pageRect = page.getBoundingClientRect();
    pointer.targetX = Math.min(1, Math.max(0, (event.clientX - pageRect.left) / Math.max(1, pageRect.width)));
    pointer.targetY = Math.min(1, Math.max(0, (event.clientY - pageRect.top) / Math.max(1, pageRect.height)));
  };

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('pointermove', onPointerMove, { passive: true });

  function draw(time) {
    if (!isVisible) {
      animationFrame = 0;
      return;
    }
    const { context, width, height } = setupCanvas(canvas, canvasRect);
    if (!context) return;

    pointer.x += (pointer.targetX - pointer.x) * 0.045;
    pointer.y += (pointer.targetY - pointer.y) * 0.045;

    context.clearRect(0, 0, width, height);
    context.globalCompositeOperation = 'lighter';

    const t = time * 0.001;
    particles.forEach((particle) => {
      const breathe = Math.sin(t * 0.85 + particle.phase) * 0.5 + 0.5;
      const baseX = particle.x * width + Math.sin(t * 0.25 + particle.phase) * 18 * particle.driftX;
      const baseY = particle.y * height + Math.cos(t * 0.3 + particle.phase) * 16 * particle.driftY;
      const dx = pointer.x * width - baseX;
      const dy = pointer.y * height - baseY;
      const distance = Math.hypot(dx, dy);
      const pull = Math.max(0, 1 - distance / Math.max(width, height) * 1.35);
      const x = baseX + dx * pull * 0.12;
      const y = baseY + dy * pull * 0.1;
      const radius = particle.radius * (1.55 + breathe * 1.2 + pull * 1.8);
      const alpha = 0.035 + breathe * 0.07 + pull * 0.13;

      const gradient = context.createRadialGradient(x, y, 0, x, y, radius * 7.2);
      const warm = particle.warmth > 0.45;
      gradient.addColorStop(0, warm ? `rgba(255, 216, 134, ${alpha})` : `rgba(255, 238, 210, ${alpha})`);
      gradient.addColorStop(0.35, warm ? `rgba(255, 151, 172, ${alpha * 0.38})` : `rgba(255, 216, 180, ${alpha * 0.34})`);
      gradient.addColorStop(1, 'rgba(255, 216, 134, 0)');

      context.fillStyle = gradient;
      context.beginPath();
      context.arc(x, y, radius * 7.2, 0, Math.PI * 2);
      context.fill();
    });

    animationFrame = requestAnimationFrame(draw);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      isVisible = entry.isIntersecting;
      if (isVisible) {
        if (!animationFrame) {
          animationFrame = requestAnimationFrame(draw);
        }
      } else {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
          animationFrame = 0;
        }
      }
    });
  }, { threshold: 0.05 });
  observer.observe(canvas);
}

function initCreditsSmoke(page) {
  const credits = page.querySelector('[data-printale-faq-credits]');
  if (!credits || prefersReducedMotion() || credits.querySelector('.printale-faq-credits__smoke')) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'printale-faq-credits__smoke';
  canvas.setAttribute('aria-hidden', 'true');
  credits.prepend(canvas);

  const paintCanvas = document.createElement('canvas');
  const paintContext = paintCanvas.getContext('2d');
  const pointer = {
    x: 0,
    y: 0,
    previousX: 0,
    previousY: 0,
    active: false,
    moved: false,
  };
  const simulation = {
    width: 0,
    height: 0,
    velocityX: new Float32Array(0),
    velocityY: new Float32Array(0),
    weight1: new Float32Array(0),
    weight2: new Float32Array(0),
    nextVelocityX: new Float32Array(0),
    nextVelocityY: new Float32Array(0),
    nextWeight1: new Float32Array(0),
    nextWeight2: new Float32Array(0),
  };
  let animationFrame = 0;
  let lastFrameTime = performance.now();
  let lastRectWidth = 0;
  let lastRectHeight = 0;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const smoothstep = (edge0, edge1, value) => {
    const t = clamp((value - edge0) / Math.max(0.0001, edge1 - edge0), 0, 1);
    return t * t * (3 - (2 * t));
  };

  const fit = (value, inMin, inMax, outMin, outMax) => {
    const t = clamp((value - inMin) / Math.max(0.0001, inMax - inMin), 0, 1);
    return outMin + ((outMax - outMin) * t);
  };

  const distanceToSegment = (px, py, ax, ay, bx, by) => {
    const vx = bx - ax;
    const vy = by - ay;
    const wx = px - ax;
    const wy = py - ay;
    const lengthSquared = (vx * vx) + (vy * vy);
    const t = lengthSquared > 0 ? clamp(((wx * vx) + (wy * vy)) / lengthSquared, 0, 1) : 0;
    const dx = px - (ax + (vx * t));
    const dy = py - (ay + (vy * t));
    return Math.hypot(dx, dy);
  };

  const sample = (field, x, y) => {
    const maxX = simulation.width - 1;
    const maxY = simulation.height - 1;
    const x0 = clamp(Math.floor(x), 0, maxX);
    const y0 = clamp(Math.floor(y), 0, maxY);
    const x1 = clamp(x0 + 1, 0, maxX);
    const y1 = clamp(y0 + 1, 0, maxY);
    const tx = clamp(x - x0, 0, 1);
    const ty = clamp(y - y0, 0, 1);
    const i00 = y0 * simulation.width + x0;
    const i10 = y0 * simulation.width + x1;
    const i01 = y1 * simulation.width + x0;
    const i11 = y1 * simulation.width + x1;
    const a = field[i00] + ((field[i10] - field[i00]) * tx);
    const b = field[i01] + ((field[i11] - field[i01]) * tx);
    return a + ((b - a) * ty);
  };

  function resizeSimulation(rect) {
    const width = Math.max(48, Math.min(220, Math.round(rect.width / 4)));
    const height = Math.max(32, Math.min(150, Math.round(rect.height / 4)));
    if (width === simulation.width && height === simulation.height) return;

    simulation.width = width;
    simulation.height = height;
    const length = width * height;
    simulation.velocityX = new Float32Array(length);
    simulation.velocityY = new Float32Array(length);
    simulation.weight1 = new Float32Array(length);
    simulation.weight2 = new Float32Array(length);
    simulation.nextVelocityX = new Float32Array(length);
    simulation.nextVelocityY = new Float32Array(length);
    simulation.nextWeight1 = new Float32Array(length);
    simulation.nextWeight2 = new Float32Array(length);
    paintCanvas.width = width;
    paintCanvas.height = height;
  }

  function swapSimulationBuffers() {
    [
      simulation.velocityX,
      simulation.nextVelocityX,
    ] = [
      simulation.nextVelocityX,
      simulation.velocityX,
    ];
    [
      simulation.velocityY,
      simulation.nextVelocityY,
    ] = [
      simulation.nextVelocityY,
      simulation.velocityY,
    ];
    [
      simulation.weight1,
      simulation.nextWeight1,
    ] = [
      simulation.nextWeight1,
      simulation.weight1,
    ];
    [
      simulation.weight2,
      simulation.nextWeight2,
    ] = [
      simulation.nextWeight2,
      simulation.weight2,
    ];
  }

  function stepSimulation(width, height, deltaTime) {
    const simWidth = simulation.width;
    const simHeight = simulation.height;
    const scaleX = simWidth / Math.max(1, width);
    const scaleY = simHeight / Math.max(1, height);
    const fromX = pointer.previousX * scaleX;
    const fromY = pointer.previousY * scaleY;
    const toX = pointer.x * scaleX;
    const toY = pointer.y * scaleY;
    const distance = Math.hypot(pointer.x - pointer.previousX, pointer.y - pointer.previousY);
    const radius = pointer.active && pointer.moved
      ? fit(distance, 0, 100, 0, Math.min(simWidth, simHeight) * 0.18)
      : 0;
    const velocityScale = Math.min(1.6, deltaTime * 0.052);
    const strokeVelocityX = clamp((toX - fromX) * velocityScale, -1.4, 1.4);
    const strokeVelocityY = clamp((toY - fromY) * velocityScale, -1.4, 1.4);
    let totalEnergy = 0;

    for (let y = 0; y < simHeight; y += 1) {
      for (let x = 0; x < simWidth; x += 1) {
        const index = y * simWidth + x;
        const sourceX = x - (simulation.velocityX[index] * 1.85);
        const sourceY = y - (simulation.velocityY[index] * 1.85);
        const vx = sample(simulation.velocityX, sourceX, sourceY) * 0.985;
        const vy = sample(simulation.velocityY, sourceX, sourceY) * 0.985;
        const w1 = sample(simulation.weight1, sourceX, sourceY) * 0.985;
        const w2 = sample(simulation.weight2, sourceX, sourceY) * 0.5;
        let draw = 0;

        if (radius > 0) {
          const segmentDistance = distanceToSegment(x, y, fromX, fromY, toX, toY);
          draw = 1 - smoothstep(-0.01, radius, segmentDistance);
        }

        simulation.nextVelocityX[index] = clamp(vx + (strokeVelocityX * draw * 0.8), -2, 2);
        simulation.nextVelocityY[index] = clamp(vy + (strokeVelocityY * draw * 0.8), -2, 2);
        simulation.nextWeight1[index] = clamp(w1 + (draw * 0.88), 0, 1);
        simulation.nextWeight2[index] = clamp(w2 + (draw * 0.72), 0, 1);
        totalEnergy += simulation.nextWeight1[index] + simulation.nextWeight2[index];
      }
    }

    swapSimulationBuffers();
    pointer.previousX = pointer.x;
    pointer.previousY = pointer.y;
    pointer.moved = false;
    return totalEnergy / Math.max(1, simWidth * simHeight);
  }

  function renderSmoke(context, width, height) {
    if (!paintContext) return;

    const image = paintContext.createImageData(simulation.width, simulation.height);
    for (let index = 0; index < simulation.weight1.length; index += 1) {
      const weight = clamp((simulation.weight1[index] + simulation.weight2[index]) * 0.5, 0, 1);
      const velocity = Math.hypot(simulation.velocityX[index], simulation.velocityY[index]);
      const shade = Math.sin((simulation.velocityX[index] + simulation.velocityY[index]) * 8);
      const alpha = clamp((weight * 205) + (velocity * 24), 0, 230);
      const offset = index * 4;

      image.data[offset] = clamp(245 + (shade * 18), 0, 255);
      image.data[offset + 1] = clamp(198 + (weight * 34), 0, 255);
      image.data[offset + 2] = clamp(184 + (velocity * 46) - (shade * 22), 0, 255);
      image.data[offset + 3] = alpha;
    }

    paintContext.putImageData(image, 0, 0);

    context.clearRect(0, 0, width, height);
    context.globalCompositeOperation = 'screen';
    context.imageSmoothingEnabled = true;

    context.globalAlpha = 0.48;
    context.filter = 'blur(18px)';
    context.drawImage(paintCanvas, -18, -18, width + 36, height + 36);

    context.filter = 'blur(8px)';
    context.globalAlpha = 0.34;
    context.drawImage(paintCanvas, 10, -4, width, height);

    context.globalAlpha = 0.28;
    context.drawImage(paintCanvas, -10, 5, width, height);

    context.filter = 'blur(2px)';
    context.globalAlpha = 0.42;
    context.drawImage(paintCanvas, 0, 0, width, height);

    context.globalAlpha = 1;
    context.filter = 'none';
  }

  let creditsRect = credits.getBoundingClientRect();
  const updateCreditsRect = () => {
    creditsRect = credits.getBoundingClientRect();
    return creditsRect;
  };
  const onResize = () => {
    updateCreditsRect();
  };
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('scroll', onResize, { passive: true });

  function draw(time) {
    const rect = updateCreditsRect();
    const { context, width, height } = setupCanvas(canvas, rect);
    if (!context) return;

    if (width !== lastRectWidth || height !== lastRectHeight) {
      resizeSimulation(rect);
      lastRectWidth = width;
      lastRectHeight = height;
    }

    const deltaTime = Math.min(32, Math.max(8, time - lastFrameTime));
    lastFrameTime = time;
    const energy = stepSimulation(width, height, deltaTime);
    renderSmoke(context, width, height);

    if (pointer.active || energy > 0.006) {
      animationFrame = requestAnimationFrame(draw);
    } else {
      context.clearRect(0, 0, width, height);
      animationFrame = 0;
    }
  }

  const start = (event) => {
    pointer.active = true;
    credits.classList.add('is-smoking');
    const rect = updateCreditsRect();
    pointer.x = event.clientX - rect.left;
    pointer.y = event.clientY - rect.top;
    pointer.previousX = pointer.x;
    pointer.previousY = pointer.y;
    pointer.moved = true;
    resizeSimulation(rect);
    lastFrameTime = performance.now();
    if (!animationFrame) animationFrame = requestAnimationFrame(draw);
  };

  const move = (event) => {
    const rect = updateCreditsRect();
    pointer.previousX = pointer.x;
    pointer.previousY = pointer.y;
    pointer.x = event.clientX - rect.left;
    pointer.y = event.clientY - rect.top;
    pointer.moved = true;
    if (!animationFrame) animationFrame = requestAnimationFrame(draw);
  };

  const stop = () => {
    pointer.active = false;
    credits.classList.remove('is-smoking');
  };

  credits.addEventListener('pointerenter', start, { passive: true });
  credits.addEventListener('pointermove', move, { passive: true });
  credits.addEventListener('pointerleave', stop, { passive: true });
}

document.querySelectorAll(pageSelector).forEach(initFaqPage);

// ─── Live timecode counter ────────────────────────────────────────────────────
// Ticks up in real-time on all visible slate meta spans so the FAQ feels alive.
(function startLiveTimecodes() {
  if (prefersReducedMotion()) return;

  const FPS = 24;
  const pageLoadTime = performance.now();

  function toTimecodeString(totalFrames) {
    const ff = totalFrames % FPS;
    let s = Math.floor(totalFrames / FPS);
    const ss = s % 60;
    let m = Math.floor(s / 60);
    const mm = m % 60;
    const hh = Math.floor(m / 60) % 24;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(hh)}:${pad(mm)}:${pad(ss)}:${pad(ff)}`;
  }

  function tick() {
    const elapsedMs = performance.now() - pageLoadTime;
    const elapsedFrames = Math.floor(elapsedMs / (1000 / FPS));

    document.querySelectorAll('.printale-faq-slate__meta').forEach((el, i) => {
      const base = 24 * FPS + i * 137;
      el.textContent = `TAKE 1 · ${toTimecodeString(base + elapsedFrames)}`;
    });

    requestAnimationFrame(tick);
  }

  // Only start once FAQ page is in view
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        requestAnimationFrame(tick);
      }
    });
  }, { threshold: 0.1 });

  const stage = document.querySelector('[data-printale-faq-stage]');
  if (stage) observer.observe(stage);
})();
